import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { Play, X, Loader2, ExternalLink } from "lucide-react";

let apiPromise = null;
const loadPlayerApi = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
    const s = document.createElement("script");
    s.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(s);
  });
  return apiPromise;
};

/** Real per-player YouTube reel. The NFL blocks off-site playback of its own uploads,
 *  so any embed error (101/150/153) falls back to a thumbnail + watch-on-YouTube card. */
export default function HighlightsButton({ player, size = 12 }) {
  const [open, setOpen] = useState(false);
  const [clip, setClip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const holder = useRef(null);

  useEffect(() => {
    if (!open || clip) return;
    setLoading(true);
    api.get(`/players/${player.player_id}/highlight`)
      .then(({ data }) => { setClip(data); setBlocked(data.embed_risk === "high"); })
      .catch(() => setClip({ kind: "stock", url: player.highlight_url }))
      .finally(() => setLoading(false));
  }, [open, clip, player]);

  useEffect(() => {
    if (!open || blocked || clip?.kind !== "youtube" || !holder.current) return;
    let instance;
    let dead = false;
    loadPlayerApi().then((YT) => {
      if (dead || !holder.current) return;
      instance = new YT.Player(holder.current, {
        videoId: clip.video_id,
        playerVars: { autoplay: 1, mute: 1, rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onError: () => {
            setBlocked(true);
            api.post(`/players/${player.player_id}/highlight/embed-blocked`).catch(() => {});
          },
        },
      });
    });
    return () => { dead = true; instance?.destroy?.(); };
  }, [open, blocked, clip]);

  const close = () => { setOpen(false); setBlocked(clip?.embed_risk === "high"); };

  if (!player?.player_id) return null;

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`highlights-${player.player_id}`}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm border border-white/20 text-white/70 hover:border-[#00FF66] hover:text-[#00FF66] hover:bg-[#00FF66]/10"
        title="Watch highlights"
      >
        <Play size={size} /> HL
      </button>
      {open && (
        <div
          className="fixed inset-0 z-[110] bg-black/90 flex items-center justify-center p-4"
          onClick={close}
          data-testid={`highlights-modal-${player.player_id}`}
        >
          <div
            className="relative w-full max-w-3xl bg-[#0a0a0a] border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3 border-b border-white/10 gap-3">
              <div className="min-w-0">
                <div className="text-[#00FF66] text-[10px] uppercase tracking-widest">Highlights</div>
                <div className="font-display text-xl truncate">{player.name} · {player.team}</div>
              </div>
              <button
                onClick={close}
                className="p-2 hover:bg-white/10 rounded-sm flex-shrink-0"
                data-testid={`highlights-close-${player.player_id}`}
              >
                <X size={16} />
              </button>
            </div>

            {loading || !clip ? (
              <div className="aspect-video bg-black flex items-center justify-center text-white/40 gap-2">
                <Loader2 size={16} className="animate-spin" /> Finding a reel...
              </div>
            ) : clip.kind === "youtube" && !blocked ? (
              <div className="w-full aspect-video bg-black" data-testid={`highlights-youtube-${player.player_id}`}>
                <div ref={holder} className="w-full h-full" />
              </div>
            ) : clip.kind === "youtube" ? (
              <a
                href={clip.watch_url || `https://www.youtube.com/watch?v=${clip.video_id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`highlights-watch-on-youtube-${player.player_id}`}
                className="group relative block w-full aspect-video bg-black overflow-hidden"
              >
                {clip.thumbnail && (
                  <img
                    src={clip.thumbnail}
                    alt={clip.title || `${player.name} highlights`}
                    className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-[1.02] transition-all duration-500"
                  />
                )}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center gap-3 px-6">
                  <div className="w-16 h-16 rounded-full bg-[#FF0000] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                    <Play size={26} className="text-white ml-0.5" />
                  </div>
                  <div className="font-display text-xl md:text-2xl drop-shadow-lg">WATCH ON YOUTUBE</div>
                  <div className="text-white/70 text-xs max-w-md">
                    The league blocks this reel from playing outside YouTube — opens in a new tab.
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#00FF66]">
                    <ExternalLink size={11} /> {clip.channel_title || "YouTube"}
                  </span>
                </div>
              </a>
            ) : (
              <video
                autoPlay
                muted
                loop
                playsInline
                controls
                data-testid={`highlights-stock-${player.player_id}`}
                className="w-full aspect-video bg-black"
                src={clip.url}
              />
            )}

            <div className="p-3 text-white/40 text-[10px] uppercase tracking-widest text-center">
              {clip?.kind === "youtube"
                ? `${clip.channel_title || "YouTube"} · ${clip.title || ""}`.slice(0, 110)
                : "Illustrative reel · real player clips appear once the YouTube key is set"}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
